import { ErrorBoundary } from '@dixa/lib-shared-habla';
import { Fragment, useEffect } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useServerStorage } from 'utils/useServerStorage';

import { NotFoundView } from '../notFound';

import { AnalyticsView } from './analytics.view';
import { AnalyticsError } from './components/error/analyticsError';
import { DashboardView } from './views/dashboard';
import { AnalyticsFeedbackView } from './views/feedback/analyticsFeedback.view';
import { IntelligenceView } from './views/intelligence/intelligence.view';
import { QuickViewView } from './views/quickView';
import { QuickViewsView } from './views/quickViews';

export const AnalyticsRoutes = (): JSX.Element => {
  const flags = useFeatureFlag([
    'custom-analytics',
    'analytics-custom-dashboards',
  ]);

  if (!flags.customAnalytics) {
    return (
      <Routes>
        <Route
          path="*"
          element={<NotFoundView navigatePath="/dashboard" name="Dashboard" />}
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path="*"
        element={
          <ErrorBoundary fallback={<AnalyticsError />}>
            <AnalyticsView />
          </ErrorBoundary>
        }
      >
        <Route index element={<RootRedirect />} />

        <Route path="dashboard" element={<DashboardView />}>
          <Route path=":slug" element={<Fragment />}>
            <Route path="feedback" element={<AnalyticsFeedbackView />} />
          </Route>
        </Route>

        <Route path="dashboards" element={<QuickViewsView />} />
        <Route path="dashboard/custom/:id" element={<QuickViewView />} />

        {flags.analyticsCustomDashboards && (
          <Route path="dashboard/insights/*" element={<IntelligenceView />} />
        )}

        {/* Redirect legacy routes to new ones. To be removed eventually. */}
        <Route
          path="quick-views"
          element={<Navigate to="/analytics/dashboards" replace />}
        />
        <Route path="quick-view/:id" element={<QuickViewViewRedirect />} />

        <Route path="*" element={<Navigate to="/analytics" replace />} />
      </Route>
    </Routes>
  );
};

/**
 * This component handles redirecting the root `/analytics` path to the landing
 * page preferred by the user if available, or to the previously viewed
 * dashboard or quick view.
 */
const RootRedirect = () => {
  const navigate = useAppNavigate();
  const flags = useFeatureFlag(['analytics-custom-dashboards']);
  const storage = useServerStorage('user');
  const { landingPageId } = storage.data.analytics ?? {};
  const { dashboard, quickView } = useAppSelector((state) => state.analytics);

  useEffect(() => {
    // If a landing page is set, redirect to it only when there is no previous dashboard or quick
    // view cached in the store. The landing page is the default view only on the first visit.
    if (
      flags.analyticsCustomDashboards &&
      landingPageId &&
      !dashboard &&
      !quickView?.id
    ) {
      return navigate(
        {
          path: '/analytics/dashboard/custom/:id',
          params: {
            path: { id: landingPageId },
          },
        },
        { replace: true },
      );
    }

    // If a quick view is cached in the Redux store, redirect to it.
    if (quickView?.id) {
      return navigate(
        {
          path: '/analytics/dashboard/custom/:id',
          params: {
            path: { id: quickView.id },
          },
        },
        { replace: true },
      );
    }

    // By default, redirect to the index dashboard route which will determine which dashboard to
    // navigate to based on whether a dashboard is cached in the Redux store.
    navigate(
      { path: '/analytics/dashboard/:slug', params: {} },
      { replace: true },
    );
  }, [
    dashboard,
    flags.analyticsCustomDashboards,
    landingPageId,
    quickView?.id,
    navigate,
  ]);

  return null;
};

/**
 * This component handles redirecting the old route `/quick-view/:id` to the new
 * route `/dashboard/custom/:id`.
 */
const QuickViewViewRedirect = () => {
  const params = useParams();
  const navigate = useAppNavigate();

  useEffect(() => {
    if (!params.id) return;

    navigate({
      path: '/analytics/dashboard/custom/:id',
      params: {
        path: {
          id: params.id,
        },
      },
    });
  }, [navigate, params.id]);

  return null;
};
